import type { DocNode } from '@atlaskit/adf-schema/doc';

import { expVal } from '@confluence/feature-experiments';

// A loader can become obsolete even when the caller stays on the same page.
export class PageRemixAdfStaleError extends Error {}

export type PageRemixLifecycleAdmissionRequest = {
	conversationId?: string;
};

export type ObjectSidebarPageRemixStartRejectionReason =
	| 'adf-load-failed'
	| 'duplicate-submit'
	| 'empty-adf'
	| 'lifecycle-rejected'
	| 'malformed-adf'
	| 'missing-identity'
	| 'not-armed'
	| 'stale-adf'
	| 'stale-open-action'
	| 'wrong-scope';

export type ObjectSidebarPageRemixStartResult =
	| {
			status: 'started';
			adfDocument: DocNode;
	  }
	| {
			status: 'rejected';
			reason: ObjectSidebarPageRemixStartRejectionReason;
	  };

type ObjectSidebarPageRemixStartOptions = {
	resolveAdfDocument: () => Promise<DocNode | null>;
	admitLifecycle?: (request: PageRemixLifecycleAdmissionRequest) => boolean;
	conversationId?: string;
	isCurrent?: () => boolean;
	isOpenActionCurrent?: () => boolean;
	onRejected: (reason: ObjectSidebarPageRemixStartRejectionReason) => void;
};

type UnknownAdfNode = {
	type: string;
	attrs?: Record<string, unknown>;
	text?: string;
	content?: UnknownAdfNode[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

// Validate with the same options the renderer uses (`editor-common` `validateADFEntity`):
// `stage0`, because Confluence renders pages with the stage-0 ADF schema (see
// `adf-renderer/ADFRendererComponent`) and without it the validator declines stage-0 specs such as
// `layoutSection_with_single_column` (1 to 5 columns, optional column divider); and
// `allowPrivateAttributes`, because Confluence page ADF carries private `__` attributes such as
// `__confluenceMetadata` on internal page links and `__expanded` on expands, which strict validation
// otherwise reports as redundant. Either omission rejects a page the product already renders as
// `malformed-adf`. Structural checks (root type, version, array content) stay in the caller; this only
// decides whether the nodes are valid ADF.
const isSchemaValidAdf = async (value: DocNode): Promise<boolean> => {
	try {
		const { validator } = await import(
			/* webpackChunkName: "loadable-adf-utils-validator" */ '@atlaskit/adf-utils/validator'
		);
		const validate = validator(undefined, undefined, {
			stage0: true,
			allowPrivateAttributes: true,
		});
		return validate(value).valid;
	} catch {
		return false;
	}
};

// Copy nodes, attributes, and marks before repairing broken media so the caller's renderer or
// editor document remains unchanged.
const cloneAdfValue = (value: unknown): unknown => {
	if (Array.isArray(value)) {
		return value.map(cloneAdfValue);
	}
	if (isRecord(value)) {
		return Object.fromEntries(
			Object.entries(value).map(([key, child]) => [key, cloneAdfValue(child)]),
		);
	}
	return value;
};

const isUnusableInternalMedia = (node: unknown): boolean => {
	if (!isRecord(node) || (node.type !== 'media' && node.type !== 'mediaInline')) {
		return false;
	}
	const { attrs } = node;
	return (
		isRecord(attrs) &&
		(attrs.type === 'file' || attrs.type === 'link') &&
		(attrs.id === undefined || attrs.id === '')
	);
};

const hasUnusableInternalMedia = (node: unknown): boolean =>
	isUnusableInternalMedia(node) ||
	(isRecord(node) && Array.isArray(node.content) && node.content.some(hasUnusableInternalMedia));

// Only repair the known missing-media-ID defect. Leave other malformed shapes in place so the
// strict schema validator still rejects them rather than admitting a silently truncated document.
const recoverBrokenMedia = (node: unknown): unknown[] => {
	if (isUnusableInternalMedia(node)) {
		return [];
	}
	if (!isRecord(node) || !Array.isArray(node.content)) {
		return [node];
	}
	const removedMediaChild = node.content.some(isUnusableInternalMedia);
	const hadMediaWrapper = node.content.some(
		(child) => isRecord(child) && (child.type === 'mediaSingle' || child.type === 'mediaGroup'),
	);
	node.content = node.content.flatMap(recoverBrokenMedia);
	if (removedMediaChild && (node.type === 'mediaSingle' || node.type === 'mediaGroup')) {
		const content = node.content as unknown[];
		if (content.length === 0) {
			return [];
		}
		if (
			node.type === 'mediaSingle' &&
			content.every((child) => isRecord(child) && child.type === 'caption')
		) {
			return content.map((caption) => ({
				...(caption as Record<string, unknown>),
				type: 'paragraph',
			}));
		}
	}
	// These block containers require at least one child. Preserve their structure when removing
	// the sole broken media wrapper, without repairing containers that were empty in the source.
	if (
		hadMediaWrapper &&
		(node.content as unknown[]).length === 0 &&
		[
			'layoutColumn',
			'listItem',
			'tableCell',
			'tableHeader',
			'panel',
			'blockquote',
			'expand',
			'nestedExpand',
			'bodiedExtension',
			'extensionFrame',
		].includes(String(node.type))
	) {
		node.content = [{ type: 'paragraph' }];
	}
	return [node];
};

const hasMeaningfulAdfContent = (node: UnknownAdfNode): boolean => {
	if (node.type === 'text') {
		return typeof node.text === 'string' && node.text.trim().length > 0;
	}
	if (!Array.isArray(node.content)) {
		return node.type !== 'paragraph' && node.type !== 'doc';
	}
	if (node.type !== 'paragraph' && node.type !== 'doc') {
		return true;
	}
	return node.content.some(hasMeaningfulAdfContent);
};

// Treatment only: recovery inserts placeholder paragraphs into emptied containers, so a container
// counts only when a descendant does. Leaves and extensions defer to the control rules above.
const hasMeaningfulDescendantContent = (node: UnknownAdfNode): boolean => {
	if (
		node.type === 'text' ||
		!Array.isArray(node.content) ||
		node.type === 'extension' ||
		node.type === 'bodiedExtension' ||
		node.type === 'multiBodiedExtension'
	) {
		return hasMeaningfulAdfContent(node);
	}
	return node.content.some(hasMeaningfulDescendantContent);
};

const rejectStart = (
	reason: ObjectSidebarPageRemixStartRejectionReason,
	onRejected: ObjectSidebarPageRemixStartOptions['onRejected'],
): ObjectSidebarPageRemixStartResult => {
	onRejected(reason);
	return { status: 'rejected', reason };
};

export const prepareObjectSidebarPageRemixStart = async ({
	resolveAdfDocument,
	admitLifecycle,
	conversationId,
	isCurrent,
	isOpenActionCurrent,
	onRejected,
}: ObjectSidebarPageRemixStartOptions): Promise<ObjectSidebarPageRemixStartResult> => {
	let resolvedAdf: DocNode | null = null;
	try {
		resolvedAdf = await resolveAdfDocument();
	} catch (error) {
		return rejectStart(
			error instanceof PageRemixAdfStaleError &&
				expVal('cc_maui_page_remix_broken_media_recovery', 'isEnabled', false)
				? 'stale-adf'
				: 'adf-load-failed',
			onRejected,
		);
	}

	if (isOpenActionCurrent && !isOpenActionCurrent()) {
		return rejectStart('stale-open-action', onRejected);
	}

	if (isCurrent && !isCurrent()) {
		return rejectStart('stale-adf', onRejected);
	}

	if (!resolvedAdf) {
		return rejectStart('empty-adf', onRejected);
	}

	if (
		!isRecord(resolvedAdf) ||
		resolvedAdf.type !== 'doc' ||
		resolvedAdf.version !== 1 ||
		!Array.isArray(resolvedAdf.content)
	) {
		return rejectStart('malformed-adf', onRejected);
	}

	let preparedAdf: DocNode = resolvedAdf;
	if (expVal('cc_maui_page_remix_broken_media_recovery', 'isEnabled', false)) {
		try {
			if (hasUnusableInternalMedia(resolvedAdf)) {
				preparedAdf = recoverBrokenMedia(cloneAdfValue(resolvedAdf))[0] as DocNode;
			}
		} catch {
			return rejectStart('malformed-adf', onRejected);
		}
	}
	const isSchemaValid = await isSchemaValidAdf(preparedAdf);
	// Importing the validator is asynchronous; the page or opening action can change while it loads.
	if (expVal('cc_maui_page_remix_broken_media_recovery', 'isEnabled', false)) {
		if (isOpenActionCurrent && !isOpenActionCurrent()) {
			return rejectStart('stale-open-action', onRejected);
		}
		if (isCurrent && !isCurrent()) {
			return rejectStart('stale-adf', onRejected);
		}
	}
	if (!isSchemaValid) {
		return rejectStart('malformed-adf', onRejected);
	}

	const isMeaningful = expVal('cc_maui_page_remix_broken_media_recovery', 'isEnabled', false)
		? preparedAdf.content.some((node: unknown) =>
				hasMeaningfulDescendantContent(node as UnknownAdfNode),
			)
		: resolvedAdf.content.some((node) => hasMeaningfulAdfContent(node as UnknownAdfNode));
	if (!isMeaningful) {
		return rejectStart('empty-adf', onRejected);
	}

	try {
		if (admitLifecycle && !admitLifecycle({ conversationId })) {
			return rejectStart('lifecycle-rejected', onRejected);
		}
	} catch {
		return rejectStart('lifecycle-rejected', onRejected);
	}

	return { status: 'started', adfDocument: preparedAdf };
};

type ObjectSidebarPageRemixNativeStartOptions = {
	startNativeRemix: () => Promise<void>;
	onNativeStartRejected: () => void;
};

export const runObjectSidebarPageRemixNativeStart = async ({
	startNativeRemix,
	onNativeStartRejected,
}: ObjectSidebarPageRemixNativeStartOptions): Promise<void> => {
	try {
		await startNativeRemix();
	} catch {
		onNativeStartRejected();
		throw new Error('Failed to start page remix from Rovo submit');
	}
};
